/**
 * @fileoverview Error handling utilities for better user experience
 * @version 1.0.0
 * @author Matthieu Chareton
 * @since 2025-07-08
 */

/**
 * Enhanced error class with user-friendly messages
 */
export class AirliftError extends Error {
  constructor(message, code = 'UNKNOWN', userMessage = null, cause = null) {
    super(message);
    this.name = 'AirliftError';
    this.code = code;
    this.userMessage = userMessage || this.getDefaultUserMessage(code);
    this.cause = cause;
    this.timestamp = new Date().toISOString();
  }

  getDefaultUserMessage(code) {
    const messages = {
      'DB_CONNECTION_FAILED': 'Unable to connect to the database. Please check your connection and try again.',
      'COLLECTION_NOT_FOUND': 'The requested data collection could not be found.',
      'VALIDATION_ERROR': 'The data you entered is invalid. Please check your input and try again.',
      'SYNC_FAILED': 'Failed to synchronize data. Your changes are saved locally and will sync when connection is restored.',
      'PERMISSION_DENIED': 'You do not have permission to perform this action.',
      'NETWORK_ERROR': 'Network connection error. Please check your internet connection.',
      'STORAGE_FULL': 'Local storage is full. Please clear some data or contact your administrator.',
      'INVALID_SCHEMA': 'The data structure is invalid. Please contact your administrator.',
      'REPLICATION_ERROR': 'Failed to replicate data with the server. Your local changes are preserved.',
      'SHAREPOINT_ERROR': 'SharePoint connection error. Please check your credentials and permissions.',
      'UNKNOWN': 'An unexpected error occurred. Please try again or contact support.'
    };
    return messages[code] || messages['UNKNOWN'];
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      userMessage: this.userMessage,
      timestamp: this.timestamp,
      cause: this.cause ? this.cause.message : null
    };
  }
}

/**
 * Database-specific error handling
 */
export class DatabaseError extends AirliftError {
  constructor(message, operation = 'unknown', cause = null) {
    const code = DatabaseError.getCodeFromMessage(message);
    const userMessage = DatabaseError.getUserMessage(code, operation);
    super(message, code, userMessage, cause);
    this.operation = operation;
  }

  static getCodeFromMessage(message) {
    if (message.includes('IndexedDB')) return 'INDEXEDDB_ERROR';
    if (message.includes('quota')) return 'STORAGE_FULL';
    if (message.includes('schema')) return 'INVALID_SCHEMA';
    if (message.includes('validation')) return 'VALIDATION_ERROR';
    if (message.includes('permission')) return 'PERMISSION_DENIED';
    return 'DB_CONNECTION_FAILED';
  }

  static getUserMessage(code, operation) {
    const operationMessages = {
      'insert': 'Failed to save new data',
      'update': 'Failed to update data',
      'delete': 'Failed to delete data',
      'query': 'Failed to retrieve data',
      'sync': 'Failed to synchronize data'
    };

    const baseMessage = operationMessages[operation] || 'Database operation failed';
    
    const codeMessages = {
      'INDEXEDDB_ERROR': `${baseMessage}. Browser storage is not available.`,
      'STORAGE_FULL': `${baseMessage}. Local storage is full.`,
      'INVALID_SCHEMA': `${baseMessage}. Data structure is invalid.`,
      'VALIDATION_ERROR': `${baseMessage}. Please check your input.`,
      'PERMISSION_DENIED': `${baseMessage}. Permission denied.`,
      'DB_CONNECTION_FAILED': `${baseMessage}. Database connection failed.`
    };

    return codeMessages[code] || baseMessage;
  }
}

/**
 * Network and replication error handling
 */
export class NetworkError extends AirliftError {
  constructor(message, url = null, status = null, cause = null) {
    const code = NetworkError.getCodeFromStatus(status);
    const userMessage = NetworkError.getUserMessage(code, url);
    super(message, code, userMessage, cause);
    this.url = url;
    this.status = status;
  }

  static getCodeFromStatus(status) {
    if (!status) return 'NETWORK_ERROR';
    if (status >= 400 && status < 500) return 'CLIENT_ERROR';
    if (status >= 500) return 'SERVER_ERROR';
    if (status === 401) return 'UNAUTHORIZED';
    if (status === 403) return 'PERMISSION_DENIED';
    if (status === 404) return 'NOT_FOUND';
    return 'NETWORK_ERROR';
  }

  static getUserMessage(code, url) {
    const messages = {
      'NETWORK_ERROR': 'Network connection failed. Please check your internet connection.',
      'CLIENT_ERROR': 'Request failed. Please check your input and try again.',
      'SERVER_ERROR': 'Server error. Please try again later.',
      'UNAUTHORIZED': 'Authentication failed. Please log in again.',
      'PERMISSION_DENIED': 'You do not have permission to access this resource.',
      'NOT_FOUND': 'The requested resource was not found.',
      'TIMEOUT': 'Request timed out. Please try again.'
    };
    
    return messages[code] || messages['NETWORK_ERROR'];
  }
}

/**
 * SharePoint-specific error handling
 */
export class SharePointError extends NetworkError {
  constructor(message, operation = 'unknown', listName = null, cause = null) {
    super(message, null, null, cause);
    this.code = 'SHAREPOINT_ERROR';
    this.operation = operation;
    this.listName = listName;
    this.userMessage = this.getSharePointUserMessage(operation, listName);
  }

  getSharePointUserMessage(operation, listName) {
    const listInfo = listName ? ` (${listName})` : '';
    const operationMessages = {
      'read': `Failed to read data from SharePoint${listInfo}`,
      'write': `Failed to save data to SharePoint${listInfo}`,
      'delete': `Failed to delete data from SharePoint${listInfo}`,
      'sync': `Failed to synchronize with SharePoint${listInfo}`,
      'auth': 'SharePoint authentication failed'
    };
    
    const baseMessage = operationMessages[operation] || 'SharePoint operation failed';
    return `${baseMessage}. Please check your connection and permissions.`;
  }
}

/**
 * Error recovery utilities
 */
export class ErrorRecovery {
  /**
   * Attempt to recover from common errors
   */
  static async attemptRecovery(error, context = {}) {
    const recovery = {
      success: false,
      action: null,
      message: null
    };

    switch (error.code) {
      case 'NETWORK_ERROR':
        recovery.action = 'retry';
        recovery.message = 'Retrying connection...';
        break;
        
      case 'STORAGE_FULL':
        recovery.action = 'cleanup';
        recovery.message = 'Cleaning up local storage...';
        break;
        
      case 'SYNC_FAILED':
        recovery.action = 'queue';
        recovery.message = 'Queuing changes for later sync...';
        break;
        
      case 'VALIDATION_ERROR':
        recovery.action = 'validate';
        recovery.message = 'Checking data format...';
        break;
        
      default:
        recovery.action = 'none';
        recovery.message = 'No automatic recovery available';
    }

    return recovery;
  }

  /**
   * Retry an operation with exponential backoff
   */
  static async retryWithBackoff(operation, maxRetries = 3, baseDelay = 1000) {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        if (attempt === maxRetries - 1) {
          throw error;
        }
        
        const delay = baseDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Check if an error is recoverable
   */
  static isRecoverable(error) {
    const recoverableCodes = [
      'NETWORK_ERROR',
      'SYNC_FAILED',
      'TIMEOUT',
      'SERVER_ERROR'
    ];
    
    return recoverableCodes.includes(error.code);
  }
}

/**
 * Error formatting utilities
 */
export class ErrorFormatter {
  /**
   * Format error for user display
   */
  static formatForUser(error) {
    if (error instanceof AirliftError) {
      return {
        title: ErrorFormatter.getTitleForCode(error.code),
        message: error.userMessage,
        code: error.code,
        timestamp: error.timestamp,
        recoverable: ErrorRecovery.isRecoverable(error)
      };
    }
    
    return {
      title: 'Unexpected Error',
      message: 'An unexpected error occurred. Please try again.',
      code: 'UNKNOWN',
      timestamp: new Date().toISOString(),
      recoverable: false
    };
  }

  /**
   * Format error for logging
   */
  static formatForLogging(error, context = {}) {
    return {
      timestamp: new Date().toISOString(),
      level: 'error',
      message: error.message,
      code: error.code || 'UNKNOWN',
      stack: error.stack,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
  }

  static getTitleForCode(code) {
    const titles = {
      'DB_CONNECTION_FAILED': 'Database Connection Error',
      'COLLECTION_NOT_FOUND': 'Data Not Found',
      'VALIDATION_ERROR': 'Invalid Data',
      'SYNC_FAILED': 'Synchronization Failed',
      'PERMISSION_DENIED': 'Permission Denied',
      'NETWORK_ERROR': 'Connection Error',
      'STORAGE_FULL': 'Storage Full',
      'SHAREPOINT_ERROR': 'SharePoint Error',
      'UNKNOWN': 'Unexpected Error'
    };
    
    return titles[code] || 'Error';
  }
}

/**
 * Global error handler setup
 */
export function setupGlobalErrorHandler() {
  window.addEventListener('error', (event) => {
    const error = new AirliftError(
      event.error?.message || 'Uncaught JavaScript error',
      'UNCAUGHT_ERROR',
      'An unexpected error occurred in the application'
    );
    
    console.error('[Global Error Handler]', ErrorFormatter.formatForLogging(error, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    }));
    
    // Could emit to error notification system here
  });

  window.addEventListener('unhandledrejection', (event) => {
    const error = new AirliftError(
      event.reason?.message || 'Unhandled promise rejection',
      'UNHANDLED_PROMISE',
      'An unexpected error occurred while processing your request'
    );
    
    console.error('[Unhandled Promise Rejection]', ErrorFormatter.formatForLogging(error));
    
    // Could emit to error notification system here
  });
}